import { DEFAULT_BRANDING_SETTINGS, type BrandingSettings, type BrandingTheme } from "@rwaforms/shared";

function toSettings(theme: BrandingTheme | null | undefined, fallback: BrandingTheme | null | undefined): BrandingSettings {
  if (theme?.settings) {
    return theme.settings;
  }
  if (fallback?.settings) {
    return fallback.settings;
  }
  return DEFAULT_BRANDING_SETTINGS;
}

export function applyBrandingTheme(theme: BrandingTheme | null | undefined, teamTheme?: BrandingTheme | null): void {
  if (typeof document === "undefined") return;
  const settings = toSettings(theme, teamTheme ?? null);

  const root = document.documentElement;
  const body = document.body;

  const primary = normalizeHex(settings.palette.primary);
  const accent = normalizeHex(settings.palette.accent);

  // Check if dark mode is active - if so, skip setting base color variables
  // to allow the CSS dark mode rules to take effect
  const isDarkMode = root.getAttribute("data-theme") === "dark";

  // Always apply brand colors (primary, accent)
  root.style.setProperty("--color-primary", primary);
  root.style.setProperty("--color-primary-soft", hexToRgba(primary, 0.12));
  root.style.setProperty("--color-accent", accent);

  // Only apply light mode base colors if NOT in dark mode
  // This allows the CSS :root[data-theme="dark"] rules to apply
  if (!isDarkMode) {
    const surface = normalizeHex(settings.palette.surface);
    const text = normalizeHex(settings.palette.text);

    root.style.setProperty("--color-surface", surface);
    root.style.setProperty("--color-bg", mixWithWhite(surface, 0.08));
    root.style.setProperty("--color-border", mixWithWhite(text, 0.8));
    root.style.setProperty("--color-border-strong", mixWithWhite(primary, 0.4));
    root.style.setProperty("--color-text", text);
    root.style.setProperty("--color-text-muted", mixWithWhite(text, 0.55));
    root.style.setProperty("--color-success", "#16a34a");
    root.style.setProperty("--color-critical", "#dc2626");
    root.style.setProperty("--shadow-sm", "0 1px 2px 0 rgba(15, 23, 42, 0.08)");
    root.style.setProperty("--shadow-md", "0 14px 32px rgba(15, 23, 42, 0.12)");
  } else {
    // In dark mode, remove any previously set inline variables to let CSS take over
    root.style.removeProperty("--color-surface");
    root.style.removeProperty("--color-bg");
    root.style.removeProperty("--color-border");
    root.style.removeProperty("--color-border-strong");
    root.style.removeProperty("--color-text");
    root.style.removeProperty("--color-text-muted");
    root.style.removeProperty("--color-success");
    root.style.removeProperty("--color-critical");
    root.style.removeProperty("--shadow-sm");
    root.style.removeProperty("--shadow-md");
  }

  // Typography and radius are always applied
  root.style.setProperty("--radius-base", `${settings.borderRadius}px`);
  root.style.setProperty("--font-family-base", settings.typography.fontFamily);
  root.style.setProperty("--font-weight-heading", settings.typography.headingWeight.toString());
  root.style.setProperty("--font-weight-body", settings.typography.bodyWeight.toString());

  body.style.fontFamily = settings.typography.fontFamily;
}

function normalizeHex(value: string): string {
  if (!value) return "#000000";
  if (value.startsWith("#") && (value.length === 4 || value.length === 7)) {
    return value;
  }
  const cleaned = value.replace(/[^a-fA-F0-9]/g, "");
  if (cleaned.length === 3 || cleaned.length === 6) {
    return `#${cleaned}`;
  }
  return "#000000";
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = normalizeHex(hex).slice(1);
  const value = normalized.length === 3 ? normalized.split("").map((char) => char + char).join("") : normalized;
  const int = parseInt(value, 16);
  return {
    r: (int >> 16) & 0xff,
    g: (int >> 8) & 0xff,
    b: int & 0xff,
  };
}

function mixWithWhite(hex: string, weight: number): string {
  const { r, g, b } = hexToRgb(hex);
  const factor = clamp(weight, 0, 1);
  const mixedR = Math.round(r + (255 - r) * factor);
  const mixedG = Math.round(g + (255 - g) * factor);
  const mixedB = Math.round(b + (255 - b) * factor);
  return `#${toHex(mixedR)}${toHex(mixedG)}${toHex(mixedB)}`;
}

function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  const clampedAlpha = clamp(alpha, 0, 1);
  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
}

function toHex(value: number): string {
  return value.toString(16).padStart(2, "0");
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
